import { Head, Link, router } from "@inertiajs/react"
import { ArrowLeft, Pencil, Printer, Trash2, FileDown, Send, Clock } from "lucide-react"
import { useState } from "react"
import { MemberLayout } from "../components/MemberLayout"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogFooter,
  ResponsiveDialogTitle,
  ResponsiveDialogDescription,
  ResponsiveDialogClose,
} from "@/components/ui/responsive-dialog"
import { CategoryBadge, UrgencyBadge, ReviewStatusBadge } from "@/components/group-features/DiscussionBadges"
import { CRUCIAL_CONVERSATION_FIELDS } from "@/lib/crucial-conversation-fields"

function formatDate(dateString) {
  const date = new Date(dateString)
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  })
}

function FieldSection({ label, value }) {
  if (!value) return null

  return (
    <div className="space-y-1">
      <p className="text-xs font-medium text-zinc-500 uppercase tracking-wide">{label}</p>
      <p className="text-sm text-zinc-700 whitespace-pre-wrap">{value}</p>
    </div>
  )
}

export default function CrucialConversationShow({ currentUser, crucialConversation }) {
  const cc = crucialConversation
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [postOpen, setPostOpen] = useState(false)

  const handleDelete = () => {
    router.delete(`/app/member/crucial_conversations/${cc.id}`, {
      onSuccess: () => setDeleteOpen(false),
    })
  }

  const handlePostToGroup = () => {
    router.post(`/app/member/crucial_conversations/${cc.id}/post_to_group`, {}, {
      onSuccess: () => setPostOpen(false),
    })
  }

  return (
    <MemberLayout currentUser={currentUser}>
      <Head title={`${cc.topicIssue} - Discussions - Kingdom Factor`} />

      <div className="max-w-2xl mx-auto space-y-6">
        {/* Back link */}
        <Link
          href="/app/member/crucial_conversations"
          className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-zinc-900 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Crucial Conversations
        </Link>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
          <div className="space-y-2 min-w-0 flex-1">
            <h1 className="text-2xl font-bold text-zinc-900 break-words">{cc.topicIssue}</h1>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-zinc-500">
              <span>{cc.author.name}</span>
              <span aria-hidden>&middot;</span>
              <span>{formatDate(cc.createdAt)}</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              <CategoryBadge category={cc.category} />
              <UrgencyBadge urgency={cc.urgency} />
              <ReviewStatusBadge reviewStatus={cc.reviewStatus} />
            </div>
          </div>

          <div className="flex shrink-0 flex-wrap gap-2 self-start sm:self-auto">
            {cc.canPostToGroup && (
              <Button
                variant="gradient"
                size="sm"
                onClick={() => setPostOpen(true)}
              >
                <Send className="h-4 w-4 sm:mr-1" />
                <span className="hidden sm:inline">Post to Group</span>
                <span className="sr-only sm:hidden">Post to Group</span>
              </Button>
            )}

            {cc.canManage && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    router.visit(`/app/member/crucial_conversations/${cc.id}/print`)
                  }
                  title="Print or save as PDF"
                >
                  <Printer className="h-4 w-4 sm:mr-1" />
                  <span className="hidden sm:inline">Print / PDF</span>
                  <span className="sr-only sm:hidden">Print or save as PDF</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    router.visit(`/app/member/crucial_conversations/${cc.id}/edit`)
                  }
                >
                  <Pencil className="h-4 w-4 sm:mr-1" />
                  <span className="hidden sm:inline">Edit</span>
                  <span className="sr-only sm:hidden">Edit</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => setDeleteOpen(true)}
                >
                  <Trash2 className="h-4 w-4 sm:mr-1" />
                  <span className="hidden sm:inline">Delete</span>
                  <span className="sr-only sm:hidden">Delete</span>
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Awaiting-review note for the owner (R16) -- the badge alone
            doesn't explain who can see it yet. */}
        {cc.isOwner && cc.reviewStatus === "pending_review" && (
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            <Clock className="h-4 w-4 mt-0.5 shrink-0" />
            <p>
              Only you and your Group&apos;s coach can see this conversation right now.
              It will become visible to the rest of your Group once a coach posts it.
            </p>
          </div>
        )}

        {/* Content */}
        <Card>
          <CardContent className="p-6 space-y-5">
            {CRUCIAL_CONVERSATION_FIELDS.map((field) => (
              <FieldSection key={field.name} label={field.label} value={cc[field.prop]} />
            ))}

            {cc.pdfFileUrl && (
              <div className="space-y-1">
                <p className="text-xs font-medium text-zinc-500 uppercase tracking-wide">
                  Supporting Document
                </p>
                <a
                  href={cc.pdfFileUrl}
                  download
                  className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 transition-colors"
                >
                  <FileDown className="h-4 w-4" />
                  {cc.pdfFileName || "Download PDF"}
                </a>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Delete confirmation */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Crucial Conversation</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this crucial conversation? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 text-white hover:bg-red-700"
              onClick={handleDelete}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Post to Group confirmation -- one-way, same irreversibility class as delete */}
      <ResponsiveDialog open={postOpen} onOpenChange={setPostOpen}>
        <ResponsiveDialogContent>
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle>Post to Group</ResponsiveDialogTitle>
            <ResponsiveDialogDescription>
              This will make the conversation visible to everyone in your Group. This
              cannot be undone.
            </ResponsiveDialogDescription>
          </ResponsiveDialogHeader>
          <ResponsiveDialogFooter>
            <ResponsiveDialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </ResponsiveDialogClose>
            <Button variant="gradient" onClick={handlePostToGroup}>
              Post to Group
            </Button>
          </ResponsiveDialogFooter>
        </ResponsiveDialogContent>
      </ResponsiveDialog>
    </MemberLayout>
  )
}
